import * as Clipboard from "expo-clipboard";
import * as MediaLibrary from "expo-media-library";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { RefObject } from "react";
import { Alert, Platform, Share, View } from "react-native";
import { captureRef } from "react-native-view-shot";

import { DEFAULT_WEB_BASE_URL } from "@/lib/config/brand";
import type { Business } from "@/lib/types";

import { readSavedCards, writeSavedCards } from "./SavedCards";

export type BusinessCardActionHandler = (
  business: Business
) => void | Promise<void>;

type Args = {
  business: Business;
  cardRef: RefObject<View | null>;
  onShare?: BusinessCardActionHandler;
  onSave?: BusinessCardActionHandler;
  onDownload?: BusinessCardActionHandler;
};

type SavedPublicBusiness = {
  businessId: string;
  slug: string;
  businessName: string;
  publicUrl: string;
  savedAt: string;
};

type CopyResult = "copied" | "manual";

const WEB_SAVED_PUBLIC_BUSINESSES_KEY = "sokoGridSavedPublicBusinesses";

function cleanText(value: unknown) {
  return String(value ?? "").trim();
}

function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

function getBusinessName(business: Business) {
  return cleanText(business.name) || "Business";
}

function getBusinessSlug(business: Business) {
  return cleanText(business.slug);
}

function getBusinessId(business: Business, publicUrl: string) {
  return (
    cleanText(business.id) ||
    cleanText(business.businessId) ||
    getBusinessSlug(business) ||
    publicUrl
  );
}

function buildSokoGridPublicBusinessUrl(slug: string) {
  return slug ? `${DEFAULT_WEB_BASE_URL}/p/${slug}` : DEFAULT_WEB_BASE_URL;
}

function isAbortError(error: unknown) {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    cleanText((error as { name?: unknown }).name) === "AbortError"
  );
}

function showMessage(title: string, message?: string) {
  if (
    Platform.OS === "web" &&
    typeof window !== "undefined" &&
    typeof window.alert === "function"
  ) {
    window.alert(message ? `${title}\n\n${message}` : title);
    return;
  }

  Alert.alert(title, message);
}

function promptForManualCopy(text: string) {
  if (
    Platform.OS === "web" &&
    typeof window !== "undefined" &&
    typeof window.prompt === "function"
  ) {
    window.prompt("Copy this Soko Grid public business link:", text);
    return true;
  }

  return false;
}

function copyWithTextarea(text: string) {
  if (
    typeof document === "undefined" ||
    !document.body ||
    typeof document.execCommand !== "function"
  ) {
    return false;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "0";

  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  const copied = document.execCommand("copy");
  textarea.remove();

  return copied;
}

async function copyTextToClipboard(text: string): Promise<CopyResult> {
  if (Platform.OS === "web") {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(text);
        return "copied";
      } catch {
        // Some browsers expose clipboard but block it outside HTTPS.
      }
    }

    if (copyWithTextarea(text)) {
      return "copied";
    }

    if (promptForManualCopy(text)) {
      return "manual";
    }

    throw new Error("Clipboard is not available in this browser.");
  }

  if (
    typeof navigator !== "undefined" &&
    navigator.clipboard
  ) {
    try {
      await navigator.clipboard.writeText(text);
      return "copied";
    } catch {
      // Continue to the Expo clipboard fallback on native-compatible targets.
    }
  }

  try {
    await Clipboard.setStringAsync(text);
    return "copied";
  } catch {
    throw new Error("Clipboard is not available on this device.");
  }
}

async function copyPublicBusinessLink(publicUrl: string) {
  const result = await copyTextToClipboard(publicUrl);

  if (result === "copied") {
    showMessage("Link copied", "The public business link was copied.");
  }
}

function readSavedPublicBusinesses() {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(WEB_SAVED_PUBLIC_BUSINESSES_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];

    if (!Array.isArray(parsed)) return [];

    return parsed.filter((item): item is SavedPublicBusiness => {
      if (!item || typeof item !== "object") return false;

      const saved = item as Partial<SavedPublicBusiness>;

      return (
        typeof saved.businessId === "string" &&
        typeof saved.slug === "string" &&
        typeof saved.businessName === "string" &&
        typeof saved.publicUrl === "string"
      );
    });
  } catch {
    return [];
  }
}

function writeSavedPublicBusinesses(next: SavedPublicBusiness[]) {
  if (typeof window === "undefined") {
    throw new Error("Saved businesses are not available in this browser.");
  }

  window.localStorage.setItem(
    WEB_SAVED_PUBLIC_BUSINESSES_KEY,
    JSON.stringify(next)
  );
}

function hasSavedPublicBusiness(publicUrl: string) {
  return readSavedPublicBusinesses().some(
    (item) => item.publicUrl === publicUrl
  );
}

function toggleSavedPublicBusiness(business: Business, publicUrl: string) {
  const savedBusinesses = readSavedPublicBusinesses();
  const existing = savedBusinesses.some((item) => item.publicUrl === publicUrl);

  if (existing) {
    writeSavedPublicBusinesses(
      savedBusinesses.filter((item) => item.publicUrl !== publicUrl)
    );

    return false;
  }

  writeSavedPublicBusinesses([
    ...savedBusinesses,
    {
      businessId: getBusinessId(business, publicUrl),
      slug: getBusinessSlug(business),
      businessName: getBusinessName(business),
      publicUrl,
      savedAt: new Date().toISOString(),
    },
  ]);

  return true;
}

export function usePublicBusinessCardActions({
  business,
  cardRef,
  onShare,
  onSave,
  onDownload,
}: Args) {
  const [saved, setSaved] = useState(false);
  const [reportVisible, setReportVisible] = useState(false);

  const publicUrl = useMemo(
    () => buildSokoGridPublicBusinessUrl(getBusinessSlug(business)),
    [business]
  );

  useEffect(() => {
    let alive = true;

    (async () => {
      const isSaved =
        Platform.OS === "web"
          ? hasSavedPublicBusiness(publicUrl)
          : (await readSavedCards()).includes(publicUrl);

      if (alive) setSaved(isSaved);
    })();

    return () => {
      alive = false;
    };
  }, [publicUrl]);

  const handleShare = useCallback(async () => {
    try {
      if (onShare) {
        await onShare(business);
        return;
      }

      const title = getBusinessName(business);

      if (Platform.OS === "web") {
        if (
          typeof navigator !== "undefined" &&
          typeof navigator.share === "function"
        ) {
          try {
            await navigator.share({
              title,
              text: `View ${title} on Soko Grid.`,
              url: publicUrl,
            });
            return;
          } catch (error) {
            if (isAbortError(error)) return;
          }
        }

        await copyPublicBusinessLink(publicUrl);
        return;
      }

      await Share.share({
        title,
        message: `Check out this business on Soko Grid:\n${publicUrl}`,
      });
    } catch (error) {
      showMessage(
        "Share failed",
        getErrorMessage(error, "Could not share this business.")
      );
    }
  }, [business, onShare, publicUrl]);

  const handleCopyLink = useCallback(async () => {
    try {
      await copyPublicBusinessLink(publicUrl);
    } catch (error) {
      console.log("Copy link failed", error);
      showMessage(
        "Copy failed",
        getErrorMessage(error, "Could not copy this business link.")
      );
    }
  }, [publicUrl]);

  const handleSave = useCallback(async () => {
    try {
      if (onSave) {
        await onSave(business);
        return;
      }

      if (Platform.OS === "web") {
        const isNowSaved = toggleSavedPublicBusiness(business, publicUrl);
        setSaved(isNowSaved);
        showMessage(isNowSaved ? "Saved" : "Removed from saved");
        return;
      }

      const arr = await readSavedCards();
      const next = saved
        ? arr.filter((link) => link !== publicUrl)
        : Array.from(new Set([...arr, publicUrl]));

      await writeSavedCards(next);
      setSaved(!saved);

      Alert.alert(saved ? "Removed from saved" : "Saved");
    } catch (error) {
      showMessage(
        "Save failed",
        getErrorMessage(error, "Could not update saved list.")
      );
    }
  }, [business, onSave, publicUrl, saved]);

  const handleReport = useCallback(() => {
    setReportVisible(true);
  }, []);

  const closeReportModal = useCallback(() => {
    setReportVisible(false);
  }, []);

  const handleDownload = useCallback(async () => {
    try {
      if (Platform.OS === "web") {
        handleReport();
        return;
      }

      if (onDownload) {
        await onDownload(business);
        return;
      }

      if (!cardRef.current) {
        throw new Error("Card image is not ready yet.");
      }

      const perm = await MediaLibrary.requestPermissionsAsync();

      if (!perm.granted) {
        Alert.alert(
          "Permission needed",
          "Allow photos permission to save the card image."
        );
        return;
      }

      const uri = await captureRef(cardRef, {
        format: "png",
        quality: 1,
      });

      const asset = await MediaLibrary.createAssetAsync(uri);
      const albumName = "sokogrid";
      const album = await MediaLibrary.getAlbumAsync(albumName);

      if (album) {
        await MediaLibrary.addAssetsToAlbumAsync([asset], album, false);
      } else {
        await MediaLibrary.createAlbumAsync(albumName, asset, false);
      }

      Alert.alert("Saved", "Card image saved to your gallery.");
    } catch (error) {
      showMessage(
        "Download failed",
        getErrorMessage(error, "Could not save image.")
      );
    }
  }, [business, cardRef, handleReport, onDownload]);

  return {
    saved,
    publicUrl,
    handleShare,
    handleCopyLink,
    handleSave,
    handleDownload,
    handleReport,
    reportModalProps: {
      business,
      publicUrl,
      visible: reportVisible,
      onClose: closeReportModal,
    },
  };
}
